import EditIcon from '@mui/icons-material/Edit'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Input } from 'sutra/components/Input/Input'
import { accents } from 'sutra/tokens/colors'

import {
  CardActionButton,
  CardActions,
  CardRoot,
  CardTitleRow,
  GroupPrefix,
  RevealOnCardHover,
} from 'components/filter-card/filter-card.styles'
import { FilterCardAccentBar } from 'components/FilterCardAccentBar'
import { InheritedFilterBadge } from 'components/InheritedFilterBadge'
import { TooltipIconButton } from 'components/TooltipIconButton'

export interface EditableTextFilterCardProps {
  title: string
  value: string
  placeholder: string
  onChange: (next: string) => void
  onClear: () => void
  // Escape reverts the caller's own buffer; the card just exits edit mode.
  onCancelEdit?: () => void
  // Fires when the user finishes an edit with a value they want applied —
  // Enter or blur, never Escape (that reverts through `onCancelEdit`).
  // Callers that apply on every keystroke (charts-v2's Smart Search debounces
  // `onChange`) simply omit it.
  onCommit?: (value: string) => void
  // Mount directly into edit mode (and focus) when `value` starts empty —
  // e.g. the moment a card first appears with nothing typed yet.
  autoEditWhenEmpty?: boolean
  clearTitle?: string
  // Whether the hover-revealed ✕ renders at all. Required so every caller
  // states its intent explicitly (e.g. Keywords' Phrase search has no
  // "re-add" affordance, so it opts out with `false`).
  removeable: boolean
  // Whether a hover-revealed reset icon renders — for cards that can't be
  // removed (`removeable={false}`) but still need a one-click way back to
  // empty. Calls the same `onClear`; it's a second trigger, not a second
  // action.
  resettable?: boolean
  // Presence renders this card read-only — inherited from a parent scope
  // rather than owned by the tile: no ✕/reset, no click-to-edit, an
  // always-visible lock badge instead. See `FilterCard.tsx`'s (charts-v2)
  // equivalent prop for the full rationale. Overrides `removeable`/
  // `resettable` rather than requiring every caller to also pass `false`.
  // `sourceLabel` is still supplied by every caller (see that file's
  // equivalent prop) but no longer read here — `InheritedFilterBadge`
  // stopped needing it.
  inherited?: { supersededNote: string | null; sourceLabel: string }
  // Ownership accent bar — see `FilterCard.tsx`'s (charts-v2) equivalent prop.
  accent?: 'local' | 'global'
  // Forwarded to the accent bar and (when `inherited`) the lock badge — see
  // `FilterCard.tsx`'s (charts-v2) equivalent props.
  tileKind?: 'chart' | 'widget'
  onEditInheritedSource?: () => void
  // The picker group this card sits under (Smart search: "Search"), shown as a
  // muted `Group > ` before the title. Omitted when hierarchy is off.
  groupLabel?: string
}

/**
 * A bordered filter card with a title, a hover-revealed ✕, and a click-to-edit
 * value chiclet that swaps to a text input while editing. Visually matches
 * charts-v2's "Smart Search" card (the shape this was extracted from); reused
 * by Keywords' "Phrase search" card.
 *
 * Fully controlled on `value` — no internal copy synced via effect. The only
 * state owned here is whether the card is in edit mode.
 */
export const EditableTextFilterCard: React.FC<EditableTextFilterCardProps> = ({
  title,
  value,
  placeholder,
  onChange,
  onClear,
  onCancelEdit,
  onCommit,
  autoEditWhenEmpty = false,
  clearTitle = 'Remove',
  removeable,
  resettable = false,
  inherited,
  accent,
  tileKind,
  onEditInheritedSource,
  groupLabel,
}) => {
  const isReadOnly = inherited != null
  const [isEditing, setIsEditing] = useState(
    () => autoEditWhenEmpty && value === '',
  )
  const inputRef = useRef<HTMLInputElement>(null)

  const commitAndExit = (): void => {
    onCommit?.(value)
    setIsEditing(false)
  }

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  return (
    <CardRoot
      $disabled={false}
      $accent={accent}
      $superseded={isReadOnly && inherited.supersededNote !== null}
    >
      {accent && (
        <FilterCardAccentBar
          accent={accent}
          tileKind={tileKind}
          onEditSource={onEditInheritedSource}
        />
      )}
      <Column>
        <Header>
          <CardTitleRow
            $disabled={false}
            $superseded={isReadOnly && inherited.supersededNote !== null}
          >
            <Title>
              {groupLabel && (
                <GroupPrefix>
                  {groupLabel} {'> '}
                </GroupPrefix>
              )}
              {title}
            </Title>
            {inherited && (
              <BadgeSlot>
                <RevealOnCardHover>
                  <InheritedFilterBadge onEditSource={onEditInheritedSource} />
                </RevealOnCardHover>
              </BadgeSlot>
            )}
          </CardTitleRow>
          {!isReadOnly && (removeable || resettable) && (
            <RevealOnCardHover>
              <CardActions>
                {resettable && (
                  <TooltipIconButton
                    tooltipTitle="Reset"
                    ariaLabel="Reset value"
                    variant="muted"
                    height={20}
                    width={20}
                    icon={<RestartAltIcon />}
                    onClick={onClear}
                  />
                )}
                {removeable && (
                  <CardActionButton
                    type="button"
                    $variant="danger"
                    aria-label={clearTitle}
                    title={clearTitle}
                    onClick={(e) => {
                      e.stopPropagation()
                      onClear()
                    }}
                  >
                    ×
                  </CardActionButton>
                )}
              </CardActions>
            </RevealOnCardHover>
          )}
        </Header>
        {isEditing && !isReadOnly ? (
          <StyledInput
            ref={inputRef}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={commitAndExit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commitAndExit()
              } else if (e.key === 'Escape') {
                onCancelEdit?.()
                setIsEditing(false)
              }
            }}
          />
        ) : (
          <ValueDisplay
            onClick={isReadOnly ? undefined : () => setIsEditing(true)}
            $readOnly={isReadOnly}
          >
            <Value $empty={!value}>{value || placeholder}</Value>
            {!isReadOnly && <ValueEditIcon />}
          </ValueDisplay>
        )}
      </Column>
    </CardRoot>
  )
}

const Column = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.span`
  font-weight: 500;
  color: #374151;
  font-size: 14px;
`

const BadgeSlot = styled.span`
  margin-left: 6px;
  display: inline-flex;
`

const StyledInput = styled(Input)`
  height: auto;
  font-size: 13px;
  padding: 4px 8px;
  line-height: 21px;
`

const ValueDisplay = styled.div<{ $readOnly?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: 1px solid transparent;
  border-radius: 4px;
  line-height: 21px;
  font-size: 13px;
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
  transition: background 0.2s ease;

  &:hover {
    background: ${({ $readOnly }) =>
      $readOnly ? 'transparent' : accents.reflectionBlue[100]};
  }
`

const Value = styled.span<{ $empty: boolean }>`
  flex: 1;
  color: ${({ $empty }) => ($empty ? '#9ca3af' : '#374151')};
  user-select: none;
`

const ValueEditIcon = styled(EditIcon)`
  font-size: 14px !important;
  color: #9ca3af;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${ValueDisplay}:hover & {
    opacity: 1;
  }
`
